import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  canRevealNext,
  canStepRelay,
  CHOICE_TIMES,
  choiceSummary,
  choiceView,
  chunkLine,
  endEventText,
  endingLabels,
  endingRowText,
  endings,
  ENDINGS,
  eventsFacts,
  eventsSummary,
  eventsTransition,
  failureSummary,
  failureTransition,
  featureLabels,
  FEATURES,
  initialChoice,
  initialEvents,
  initialFailure,
  initialRelay,
  NOT_PLAYED,
  pageText,
  pieceCount,
  piecesShown,
  piecesText,
  PLAIN_DIGEST,
  PLAIN_TIMELINE,
  RELAY_DESIGNS,
  relayDesignLabels,
  relayRowText,
  relaySummary,
  relayTransition,
  replyLength,
  requestConnectionLabel,
  revealedChunks,
  revealedSteps,
  secondsLabel,
  toEnding,
  toFeature,
  toRelayDesign,
  type EventsAction,
  type FailureAction,
  type RelayAction,
} from './stream-models';

import './relationship-labs.css';
import './stream-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
  className = '',
  empty = 'No rows',
}: {
  label: string;
  columns: readonly string[];
  rows: (string | number)[][];
  template?: string;
  className?: string;
  empty?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className={`rel-table cb-record-grid ${className}`} role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">{empty}</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const REPLAY = 'This graphic replays real requests.';

export function StreamEventsLab() {
  const [state, setState] = useState(initialEvents);
  const act = (action: EventsAction) => setState((current) => eventsTransition(current, action));
  const chunks = revealedChunks(state);
  const summary = eventsSummary(state);
  return (
    <Lab
      title="What arrives, and when, once the reply streams?"
      eyebrow="Streaming · the events"
      className="rel-lab stream-lab"
    >
      <Predict>
        When do the first words reach the page? Press stream: true, then press Next chunk for each
        line.
      </Predict>
      <div className="cb-controls">
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.streaming}
          onClick={() => act({ type: 'toggle' })}
        >
          stream: true
        </Button>
        <Button size="sm" disabled={!canRevealNext(state)} onClick={() => act({ type: 'next' })}>
          Next chunk
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      {state.streaming ? (
        <>
          <Table
            label="Event stream"
            className="stream-mono"
            columns={['At', 'Line']}
            template="minmax(0, 0.6fr) minmax(0, 6fr)"
            rows={chunks.map((chunk) => [secondsLabel(chunk.at), chunkLine(chunk)])}
            empty="No chunk yet"
          />
          <Panel label="Page so far">
            <pre aria-label="Page so far">{pageText(state)}</pre>
          </Panel>
        </>
      ) : (
        <>
          <Panel label="Timeline">
            <pre aria-label="Timeline">{PLAIN_TIMELINE}</pre>
          </Panel>
          <Panel label="Reply">
            <pre aria-label="Reply">{PLAIN_DIGEST}</pre>
          </Panel>
        </>
      )}
      <Facts values={eventsFacts(state)} />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function StreamRelayLab() {
  const [state, setState] = useState(initialRelay);
  const act = (action: RelayAction) => setState((current) => relayTransition(current, action));
  const shown = piecesShown(state.step);
  const summary = relaySummary(state);
  return (
    <Lab
      title="Which connection writes the row when the body streams?"
      eyebrow="Streaming · the relay"
      className="rel-lab stream-lab"
    >
      <Predict>
        Is the request's connection still open when the row is written? Press Next step to the end,
        then change Row written with.
      </Predict>
      <div className="cb-controls">
        <label>
          Row written with
          <ModelSelect
            aria-label="Row written with"
            value={state.design}
            onValueChange={(value) => act({ type: 'design', value: toRelayDesign(value) })}
            options={RELAY_DESIGNS.map((design) => ({
              value: design,
              label: relayDesignLabels[design],
            }))}
          />
        </label>
        <Button size="sm" disabled={!canStepRelay(state)} onClick={() => act({ type: 'next' })}>
          Next step
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="rel-flow" aria-label="Hops">
        <span>
          Browser
          <small>{pieceCount(shown)} on the page</small>
        </span>
        <span aria-hidden="true">→</span>
        <span data-token="true">
          Our API
          <small>the token lives here</small>
        </span>
        <span aria-hidden="true">→</span>
        <span>
          Gateway
          <small>the model's stream</small>
        </span>
      </div>
      <Table
        label="Steps"
        className="stream-steps"
        columns={['Step', 'What happens']}
        template="minmax(0, 0.5fr) minmax(0, 7fr)"
        rows={revealedSteps(state).map((step, index) => [index + 1, step])}
        empty="No step yet"
      />
      <div className="cb-two">
        <Panel label="Request connection">
          <pre aria-label="Request connection">{requestConnectionLabel(state.step)}</pre>
        </Panel>
        <Panel label="Row">
          <pre aria-label="Row">{relayRowText(state)}</pre>
        </Panel>
      </div>
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function StreamFailureLab() {
  const [state, setState] = useState(initialFailure);
  const act = (action: FailureAction) => setState((current) => failureTransition(current, action));
  const record = endings[state.ending];
  const summary = failureSummary(state);
  return (
    <Lab
      title="How does a stream end, and what is left behind?"
      eyebrow="Streaming · how it ends"
      className="rel-lab stream-lab"
    >
      <Predict>
        Does every ending send an end event and leave a row? Choose an ending in What happens, then
        press Play.
      </Predict>
      <div className="cb-controls">
        <label>
          What happens
          <ModelSelect
            aria-label="What happens"
            value={state.ending}
            onValueChange={(value) => act({ type: 'ending', value: toEnding(value) })}
            options={ENDINGS.map((ending) => ({ value: ending, label: endingLabels[ending] }))}
          />
        </label>
        <Button size="sm" disabled={state.played} onClick={() => act({ type: 'play' })}>
          Play
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="Pieces">
        <pre aria-label="Pieces">{state.played ? piecesText(state.ending) : NOT_PLAYED}</pre>
      </Panel>
      <div className="cb-two">
        <Panel label="End event">
          <pre aria-label="End event">{state.played ? endEventText(state.ending) : NOT_PLAYED}</pre>
        </Panel>
        <Panel label="Row">
          <pre aria-label="Row">{state.played ? endingRowText(state.ending) : NOT_PLAYED}</pre>
        </Panel>
      </div>
      {state.played ? (
        <Facts
          values={[
            ['Pieces on the page', String(record.pieces)],
            ['Reply stored', `${replyLength(record.pieces)} characters`],
            ['Outcome', record.outcome],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function StreamChoiceLab() {
  const [state, setState] = useState(initialChoice);
  const view = choiceView(state.feature, state.streaming);
  const summary = choiceSummary(state);
  return (
    <Lab
      title="Which features should stream?"
      eyebrow="Streaming · what to stream"
      className="rel-lab stream-lab"
    >
      <Predict>
        Which feature has something useful at 1.0 s? Choose a Feature, then press stream to compare.
      </Predict>
      <div className="cb-controls">
        <label>
          Feature
          <ModelSelect
            aria-label="Feature"
            value={state.feature}
            onValueChange={(value) =>
              setState((current) => ({ ...current, feature: toFeature(value) }))
            }
            options={FEATURES.map((feature) => ({ value: feature, label: featureLabels[feature] }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.streaming}
          onClick={() => setState((current) => ({ ...current, streaming: !current.streaming }))}
        >
          stream
        </Button>
        <Reset onClick={() => setState(initialChoice())} />
      </div>
      <Table label="What the page shows" columns={CHOICE_TIMES} rows={[[...view.cells]]} />
      <Facts
        values={[
          ['Done at', view.doneAt],
          ['Gain from streaming', view.gain],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
